import React, { memo } from 'react';
import { Tooltip, Typography, Chip, Divider, useTheme } from '@material-ui/core';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import HeightIcon from '@material-ui/icons/Height';
import { useTranslation } from 'react-i18next';
import { useStyles } from '../DiagnosticRevisionTable.style';
import { diagnosticsClassColor } from '../../../../../../helpers/manageColors';

const HistoryCell = ({ nested, matchNestedTable, isExpanded, lesionPriors }) => {
  const classes = useStyles();
  const theme = useTheme();
  const { t } = useTranslation();

  const getDate = (date) => {
    if (!date) return '-';
    if (typeof date !== 'string') return date;
    return date.split(' ')[0];
  };

  if ((nested && !matchNestedTable) || isExpanded) {
    return '-';
  }

  if (lesionPriors?.length === 0) {
    return <div className={classes.empty_cell_container}>-</div>;
  } else if (lesionPriors?.length === 1) {
    return (
      <div className={classes.empty_cell_container}>
        <Tooltip
          arrow
          title={
            <div>
              <Typography className={classes.tooltip_text}>
                {lesionPriors?.[0]?.is_new ? t('diagnose.new_lesion_info') : ''}
              </Typography>
              {(lesionPriors?.[0]?.date || lesionPriors?.[0]?.study_date) && (
                <Typography className={classes.tooltip_text}>
                  - {t('Table.date')}: {getDate(lesionPriors?.[0]?.date || lesionPriors?.[0]?.study_date)}
                </Typography>
              )}
              {lesionPriors?.[0]?.object_id && (
                <Typography className={classes.tooltip_text}>
                  - {t('diagnose.object_id')}: {lesionPriors?.[0]?.object_id}
                </Typography>
              )}
            </div>
          }>
          <div className={classes.history_item_container}>
            <Typography className={classes.history_date_label}>
              {lesionPriors?.[0]?.is_new
                ? ''
                : getDate(lesionPriors?.[0]?.date || lesionPriors?.[0]?.study_date)}
            </Typography>
            <Chip
              id='history-chip'
              label={
                <div className={classes.chip_container}>
                  <Typography className={classes.class_chip_text}>
                    {lesionPriors?.[0]?.not_found
                      ? 'N/A'
                      : lesionPriors?.[0]?.is_new || lesionPriors?.[0]?.birads === 'birads1'
                        ? 'N/A'
                        : `${lesionPriors?.[0]?.birads?.slice(6, 7)}`}
                  </Typography>
                </div>
              }
              size='small'
              variant='outlined'
              className={classes.history_chip}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                if (lesionPriors?.[0]?.is_new || lesionPriors?.[0]?.birads === 'birads1') {
                  return;
                }
                window.open(`/bbox/results/details/${lesionPriors?.[0]?.study_id}`, '_blank');
              }}
              style={{
                color:
                  lesionPriors?.[0]?.is_new || lesionPriors?.[0]?.birads === 'birads1'
                    ? 'white'
                    : `${diagnosticsClassColor(lesionPriors?.[0]?.birads, theme)}`,
                border:
                  lesionPriors?.[0]?.is_new || lesionPriors?.[0]?.birads === 'birads1'
                    ? '1px solid #339583'
                    : `1px solid ${diagnosticsClassColor(lesionPriors?.[0]?.birads, theme)}`,
                cursor:
                  lesionPriors?.[0]?.is_new || lesionPriors?.[0]?.birads === 'birads1'
                    ? 'default'
                    : 'pointer',
              }}
            />
            <div className={classes.history_size_container}>
              <Typography className={classes.history_size_text}>
                {lesionPriors?.[0].matched_size && !isExpanded && !nested
                  ? `${lesionPriors?.[0].matched_size?.[0]?.toFixed(
                      0,
                    )} x ${lesionPriors?.[0].matched_size?.[1]?.toFixed(0)} mm`
                  : lesionPriors?.[0].size
                    ? `${lesionPriors?.[0].size?.[0]?.toFixed(0)}x${lesionPriors?.[0].size?.[1]?.toFixed(
                        0,
                      )}mm ${
                        lesionPriors?.[0].size_change_latest
                          ? `(${lesionPriors?.[0].size_change_latest?.toFixed(0)}%)`
                          : ''
                      } ${
                        lesionPriors?.[0].size_change_latest
                          ? lesionPriors?.[0].size_change_latest > 0
                            ? '↑'
                            : lesionPriors?.[0].size_change_latest < 0
                              ? '↓'
                              : '↔'
                          : ''
                      }`
                    : '-'}
              </Typography>
            </div>
          </div>
        </Tooltip>
      </div>
    );
  }

  // Multiple priors
  return (
    <div className={classes.history_timeline_container}>
      {/* Timeline line behind chips */}
      <div className={classes.history_timeline_line} />

      {lesionPriors?.map((prior, index) => (
        <Tooltip
          key={prior?.study_id || index}
          title={
            prior?.not_found ? (
              <div>
                <Typography className={classes.tooltip_text} style={{ fontWeight: 'bold' }}>
                  {t('Filters.not_found')}
                </Typography>
                <Typography className={classes.tooltip_text}>
                  - {t('Table.date')}: {getDate(prior?.date || prior?.study_date)}
                </Typography>
              </div>
            ) : (
              <div>
                {prior?.birads === 'birads1' ? (
                  <Typography className={classes.tooltip_text} style={{ fontWeight: 'bold' }}>
                    {t('diagnose.new_lesion_info')}
                  </Typography>
                ) : (
                  <Typography className={classes.tooltip_text} style={{ fontWeight: 'bold' }}>
                    {t('diagnose.open_previous_study')}:
                  </Typography>
                )}
                <Typography className={classes.tooltip_text}>
                  - {t('Table.date')}: {getDate(prior?.date || prior?.study_date)}
                </Typography>
                {prior?.object_id && (
                  <Typography className={classes.tooltip_text}>
                    - {t('diagnose.object_id')}: {prior?.object_id || 'N/A'}
                  </Typography>
                )}
                {prior?.size && (
                  <Typography className={classes.tooltip_text}>
                    {`- ${t('diagnose.size')}: ${prior?.size?.[0]?.toFixed(0)} x
                  ${prior?.size?.[1]?.toFixed(0)} mm  ${
                    prior?.size_change_latest
                      ? `(${prior?.size_change_latest > 0 ? '↑' : prior?.size_change_latest < 0 ? '↓' : '↔'}
                    ${prior?.size_change_latest?.toFixed(0)}%)`
                      : ''
                  }`}
                  </Typography>
                )}
              </div>
            )
          }
          placement='top'
          arrow>
          <div
            className={classes.history_item_container}
            onPointerDown={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              window.open(`/bbox/results/details/${prior?.study_id}`, '_blank');
            }}>
            {/* Date label */}
            <Typography className={classes.history_date_label}>
              {getDate(prior?.date || prior?.study_date)}
            </Typography>

            {/* History Class Chip */}
            <Chip
              label={
                <div>
                  {prior?.not_found ? (
                    'n/a'
                  ) : (
                    <div className={classes.chip_container}>
                      <Typography className={classes.class_chip_text}>
                        {prior?.birads ? (prior?.birads === 'birads1' ? '-' : prior?.birads.slice(6, 7)) : ''}
                      </Typography>
                      {prior?.severity_change_latest ? (
                        prior?.severity_change_latest > 0 ? (
                          <ArrowUpwardIcon className={classes.severity_change_icon} />
                        ) : prior?.severity_change_latest < 0 ? (
                          <ArrowDownwardIcon className={classes.severity_change_icon} />
                        ) : (
                          <HeightIcon
                            className={classes.severity_change_icon}
                            style={{ transform: 'rotate(90deg)' }}
                          />
                        )
                      ) : (
                        ''
                      )}
                    </div>
                  )}
                </div>
              }
              size='small'
              variant='outlined'
              className={classes.history_chip}
              style={{
                color: prior?.is_new
                  ? '#339583'
                  : prior?.birads === 'birads1'
                    ? '#5b5b5b'
                    : diagnosticsClassColor(prior?.birads, theme) || 'white',
                border: `1px solid ${
                  prior?.is_new
                    ? '#339583'
                    : prior?.birads === 'birads1'
                      ? '#5b5b5b'
                      : `${diagnosticsClassColor(prior?.birads, theme)}` || theme.palette.grey[500]
                }`,
              }}
            />

            {/* Size below chip */}
            <div className={classes.history_size_container}>
              <Typography className={classes.history_size_text}>
                {prior?.matched_size && !isExpanded && !nested
                  ? `${prior?.matched_size?.[0]?.toFixed(0)} x ${prior?.matched_size?.[1]?.toFixed(0)} mm`
                  : prior?.size
                    ? `${prior?.size?.[0]?.toFixed(0)}x${prior?.size?.[1]?.toFixed(0)}mm ${
                        prior?.size_change_latest ? `(${prior?.size_change_latest?.toFixed(0)}%)` : ''
                      } ${
                        prior?.size_change_latest
                          ? prior?.size_change_latest > 0
                            ? '↑'
                            : prior?.size_change_latest < 0
                              ? '↓'
                              : '↔'
                          : ''
                      }`
                    : '-'}
              </Typography>
            </div>
          </div>
        </Tooltip>
      ))}
    </div>
  );
};

export default memo(HistoryCell);
